import { motion } from "framer-motion";
import { ArrowUpRight, BedDouble, Building2, Car, Layers, LayoutGrid, Sofa } from "lucide-react";

const FABRIC_IMG =
  "https://images.unsplash.com/photo-1583151923262-1aadb814995a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxuZXV0cmFsJTIwY2xlYW4lMjBmYWJyaWMlMjB0ZXh0aWxlJTIwY2xvc2UlMjB1cCUyMHRleHR1cmV8ZW58MHx8fHwxNzkxNzE3MjI2fDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    id: "canapes",
    icon: Sofa,
    title: "Canapés & Fauteuils",
    desc: "Du fauteuil au canapé d'angle : injection-extraction en profondeur, détachage des assises et neutralisation des odeurs, coussins inclus.",
    hint: "dès 20,00 €",
    span: "md:col-span-7",
    image: FABRIC_IMG,
  },
  {
    id: "matelas",
    icon: BedDouble,
    title: "Matelas & Literie",
    desc: "Matelas toutes tailles (du 90 x 190 au King size), sommiers, têtes de lit et coussins — assainissement anti-acariens, 2 faces.",
    hint: "dès 10,00 €",
    span: "md:col-span-5",
  },
  {
    id: "moquettes",
    icon: LayoutGrid,
    title: "Moquettes (logement)",
    desc: "Pièces de vie, chambres, couloirs, escaliers — shampooing injection-extraction et redressement des fibres, au m².",
    hint: "dès 4,50 € / m²",
    span: "md:col-span-4",
  },
  {
    id: "auto",
    icon: Car,
    title: "Habitacle Auto",
    desc: "Sièges, banquettes, moquettes de sol, coffre, siège enfant — nettoyage complet du textile automobile, odeurs de tabac ou d'animaux éliminées.",
    hint: "dès 15,00 €",
    span: "md:col-span-4",
  },
  {
    id: "tapis",
    icon: Layers,
    title: "Tapis",
    desc: "Tapis synthétiques, laine ou poils longs — nettoyage doux adapté à chaque fibre, sur place ou emporté.",
    hint: "dès 9,00 € / m²",
    span: "md:col-span-4",
  },
  {
    id: "entreprises",
    icon: Building2,
    title: "Service Pro & Entreprises",
    desc: "Hôtels, restaurants, bureaux, salles d'attente — chaises, banquettes, matelas et moquettes en volume. Tarifs dégressifs, interventions hors horaires, contrats d'entretien réguliers.",
    hint: "dès 4,00 € / m²",
    span: "md:col-span-12",
    dark: true,
  },
];

export default function Services() {
  return (
    <section id="services" className="py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-teal-700 sm:text-sm">
            Nos services
          </p>
          <h2 className="font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Six spécialités, une même exigence
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Tout le textile de votre intérieur et de votre véhicule, nettoyé en
            profondeur par injection-extraction — sans rien déplacer de chez vous.
          </p>
        </motion.div>

        <div data-testid="services-grid" className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          {services.map((s, i) => (
            <motion.a
              key={s.id}
              href="#tarifs"
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className={`group relative overflow-hidden rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                s.dark
                  ? "bg-[#0B131E] text-white shadow-lg shadow-slate-900/20"
                  : "border border-slate-200 bg-white shadow-sm"
              } ${s.span}`}
            >
              {s.image && (
                <div className="absolute inset-0">
                  <img src={s.image} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/30" />
                </div>
              )}
              <div className="relative">
                <div className="mb-6 flex items-start justify-between">
                  <span
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${
                      s.dark ? "bg-teal-500/15 text-teal-300" : "bg-teal-50 text-teal-700"
                    }`}
                  >
                    <s.icon className="h-6 w-6" />
                  </span>
                  <ArrowUpRight
                    className={`h-5 w-5 transition-all duration-200 group-hover:translate-x-1 group-hover:-translate-y-1 ${
                      s.dark ? "text-teal-300" : "text-teal-600"
                    }`}
                  />
                </div>
                <h3
                  className={`font-serif text-2xl font-semibold tracking-tight sm:text-3xl ${
                    s.dark ? "text-white" : "text-slate-900"
                  }`}
                >
                  {s.title}
                </h3>
                <p
                  className={`mt-3 max-w-2xl text-sm leading-relaxed sm:text-base ${
                    s.dark ? "text-slate-300" : "text-slate-600"
                  }`}
                >
                  {s.desc}
                </p>
                <span
                  className={`mt-6 inline-block rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider ${
                    s.dark ? "bg-teal-500/15 text-teal-300" : "bg-teal-50 text-teal-700"
                  }`}
                >
                  {s.hint}
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
